import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { booleanAttribute, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';

let nextDialogId = 0;

/**
 * Modal de verdade (overlay, foco preso, `aria-modal`, Esc e rolagem do fundo travada).
 * Reservado às confirmações críticas e aos relatórios que pedem dados ao usuário
 * (decisão 2026-10-24-modais-voltam-para-confirmacoes-criticas-e-relatorios).
 *
 * Uso: `<ss-dialog [open]="aberto" title="..." (closed)="aberto = false">corpo<div ssDialogActions>botões</div></ss-dialog>`
 */
@Component({
  selector: 'ss-dialog',
  templateUrl: './dialog.component.html',
  styleUrls: ['./dialog.component.scss'],
  standalone: false
})
export class DialogComponent implements OnChanges, OnDestroy {
  @Input() open = false;
  @Input() title = '';
  @Input() icon = '';
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  @Input() tone: 'default' | 'danger' | 'warning' = 'default';
  /** Enquanto ocupado (ex.: salvando), Esc, X e o fundo não fecham. */
  @Input({ transform: booleanAttribute }) busy = false;
  @Input({ transform: booleanAttribute }) closeOnBackdrop = false;
  /** Corpo sem rolagem própria, para conteúdo com popups (calendário, listas de select) que não podem ser cortados. */
  @Input({ transform: booleanAttribute }) overflowVisible = false;
  @Output() closed = new EventEmitter<void>();

  @ViewChild('content', { static: true }) content!: TemplateRef<unknown>;

  readonly titleId = `ss-dialog-title-${nextDialogId++}`;
  private overlayRef?: OverlayRef;
  private previouslyFocused: HTMLElement | null = null;

  constructor(private overlay: Overlay, private viewContainerRef: ViewContainerRef) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['open']) return;
    if (this.open) this.attach();
    else this.detach();
  }

  ngOnDestroy(): void {
    this.detach();
  }

  requestClose(): void {
    if (!this.busy) this.closed.emit();
  }

  private attach(): void {
    if (this.overlayRef) return;
    this.previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      usePopover: false,
      backdropClass: 'ss-dialog__backdrop',
      panelClass: 'ss-dialog__pane',
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.block()
    });
    this.overlayRef.attach(new TemplatePortal(this.content, this.viewContainerRef));
    this.overlayRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.requestClose();
      }
    });
    this.overlayRef.backdropClick().subscribe(() => {
      if (this.closeOnBackdrop) this.requestClose();
    });
  }

  private detach(): void {
    if (!this.overlayRef) return;
    this.overlayRef.dispose();
    this.overlayRef = undefined;
    this.previouslyFocused?.focus?.();
    this.previouslyFocused = null;
  }
}
